/**
 * Shipping pricing model — the single source of truth for how a basket of books
 * becomes a set of parcels and a price.
 *
 * See SHIPPING_PRICING.md. In brief (§A3): lockers are 38 x 64 cm across and
 * differ only in depth, so books are assumed packed flat in layers, two side by
 * side where they fit. The depth of the stack picks the container.
 *
 * CommonJS on purpose. The server is plain Node and cannot `require` the ES
 * modules under `src/`, but it can require this file by path; the client build
 * only resolves imports under `src/`. CommonJS in `src/util/` is the one shape
 * both sides can load, which is what keeps this table from being duplicated
 * (it previously existed in three places, drifting).
 */

// ---------------------------------------------------------------------------
// Delivery methods
// ---------------------------------------------------------------------------

const DPD_KEY = 'dpd-parcel-shop';
const YODEL_HOME_KEY = 'yodel-store-to-home';
const YODEL_STORE_KEY = 'yodel-store-to-store';
const INPOST_LOCKER_TO_HOME_KEY = 'inpost-locker-to-home';
const INPOST_LOCKER_TO_LOCKER_KEY = 'inpost-locker-to-locker';
const SELF_SHIPPING_KEY = 'organise-postage-yourself';

// Display order wherever the full list of methods is rendered.
const shippingOptionOrder = [
  DPD_KEY,
  INPOST_LOCKER_TO_HOME_KEY,
  INPOST_LOCKER_TO_LOCKER_KEY,
  SELF_SHIPPING_KEY,
  YODEL_HOME_KEY,
  YODEL_STORE_KEY,
];

// Methods whose shipping fee is charged to the customer only: the marketplace
// settles with Moov, so the money never reaches the seller. InPost and
// self-shipping fees flow to the seller, who buys the postage themselves.
const shipmentsForAdmin = [DPD_KEY, YODEL_HOME_KEY, YODEL_STORE_KEY];

const INPOST_METHODS = [INPOST_LOCKER_TO_HOME_KEY, INPOST_LOCKER_TO_LOCKER_KEY];

const isInPost = method => INPOST_METHODS.includes(method);

// Per-option kill switches. Every option is ON by default; set the env var to a
// falsy value (off/false/0/no) to hide one. Only add a var for options you want
// OFF.  e.g.  REACT_APP_SHIPPING_YODEL=off
//
// NB: must be read via static `process.env.REACT_APP_*` access — the client
// build only inlines that literal form, not dynamic `process.env[key]` lookups.
const isOff = value => ['off', 'false', '0', 'no'].includes(String(value).trim().toLowerCase());

const shippingOptionDisabled = {
  [DPD_KEY]: isOff(process.env.REACT_APP_SHIPPING_DPD),
  [YODEL_HOME_KEY]: isOff(process.env.REACT_APP_SHIPPING_YODEL),
  [YODEL_STORE_KEY]: isOff(process.env.REACT_APP_SHIPPING_YODEL),
  [INPOST_LOCKER_TO_HOME_KEY]: isOff(process.env.REACT_APP_SHIPPING_INPOST_HOME),
  [INPOST_LOCKER_TO_LOCKER_KEY]: isOff(process.env.REACT_APP_SHIPPING_INPOST_LOCKER),
  [SELF_SHIPPING_KEY]: isOff(process.env.REACT_APP_SHIPPING_SELF),
};

const isShippingOptionDisabled = method => !!shippingOptionDisabled[method];

// ---------------------------------------------------------------------------
// Rates (£ per parcel/locker) — §A9
// ---------------------------------------------------------------------------

// DPD/Yodel rows are what Shimmy pays and settles with Moov. InPost rows are
// what the SELLER pays retail at the locker, so stale InPost numbers come out
// of every seller's pocket on every order. See open question 7.
const RATES = {
  [DPD_KEY]: { small: 3.95, medium: 4.95, large: 5.75 },
  [YODEL_HOME_KEY]: { small: 2.75, medium: 3.1, large: 4.0 },
  [YODEL_STORE_KEY]: { small: 2.35, medium: 2.6, large: 2.85 },
  [INPOST_LOCKER_TO_HOME_KEY]: { small: 2.89, medium: 3.99, large: 5.99 },
  [INPOST_LOCKER_TO_LOCKER_KEY]: { small: 1.99, medium: 2.59, large: 3.99 },
};

// Smallest first. Order matters: tier selection and the "worse of two tiers"
// lift both rely on it.
const TIERS = ['small', 'medium', 'large'];

// ---------------------------------------------------------------------------
// The book table — §A3, §A5
// ---------------------------------------------------------------------------

// Thickness in cm by size and format.
// VERIFY: desk estimates sanity-checked against printers' spine calculators,
// not measured. Checklist item 1.
const THICKNESS_CM = {
  small: { paperback: 2.0, hardback: 2.5 },
  medium: { paperback: 2.5, hardback: 3.5 },
  large: { paperback: 3.5, hardback: 5.0 },
};

// How many of a given size fit side by side in one layer. Deliberately modest:
// a locker's face could take more, but two across is a box shape sellers
// actually use, and InPost is the tier where the seller pays for optimism.
const SIDE_BY_SIDE = { small: 2, medium: 2, large: 1 };

// Weight in grams. VERIFY: checklist item 2.
const WEIGHT_G = {
  small: { paperback: 200, hardback: 450 },
  medium: { paperback: 350, hardback: 700 },
  large: { paperback: 600, hardback: 1200 },
};

const BOOK_SIZES = ['small', 'medium', 'large'];
const BOOK_FORMATS = ['paperback', 'hardback'];

// Defaults when listing data is incomplete — §A3. Never default to the
// cheapest: overcharging slightly is recoverable, under-sizing a locker the
// seller then pays for is not.
const DEFAULT_SIZE_FOR_FORMAT = { paperback: 'small', hardback: 'medium' };
const FALLBACK_SIZE = 'large';
const FALLBACK_FORMAT = 'hardback';

/**
 * Resolve a listing's size/format to its physical numbers, filling gaps by
 * whatever is known (§A3).
 *
 * @param {Object} book
 * @param {string} [book.size] - 'small' | 'medium' | 'large'
 * @param {string} [book.format] - 'paperback' | 'hardback'
 * @returns {{size: string, format: string, thicknessCm: number, sideBySide: number, weightG: number}}
 */
const resolveBook = book => {
  const format = BOOK_FORMATS.includes(book && book.format) ? book.format : null;
  const declaredSize = BOOK_SIZES.includes(book && book.size) ? book.size : null;

  // Format known, size missing -> the standard size for that format (the same
  // default the listing form applies). Format unknown too -> the most expensive
  // plausible book.
  const resolvedFormat = format || FALLBACK_FORMAT;
  const resolvedSize =
    declaredSize || (format ? DEFAULT_SIZE_FOR_FORMAT[format] : FALLBACK_SIZE);

  return {
    // Carried through so a packing can say which listing each book came from —
    // the seller's packing list and the per-parcel label contents both need it.
    listingId: book && book.listingId,
    size: resolvedSize,
    format: resolvedFormat,
    thicknessCm: THICKNESS_CM[resolvedSize][resolvedFormat],
    sideBySide: SIDE_BY_SIDE[resolvedSize],
    weightG: WEIGHT_G[resolvedSize][resolvedFormat],
  };
};

// ---------------------------------------------------------------------------
// Container capacities — §A4
// ---------------------------------------------------------------------------

// InPost locker depths, verified against inpost.co.uk 2026-08-26. Physical and
// fixed; the lockers are 38 x 64 cm across, which never binds for books.
const LOCKER_DEPTH_CM = { small: 8, medium: 19, large: 41 };

// The seller's protection: slack between what we price and what physically
// fits. A tunable risk appetite, NOT a measurement — keep it a reviewed code
// constant rather than an env var, since changing it changes prices.
const INPOST_MARGIN = 0.15;

// DPD/Yodel: deliberate loose placeholders seeded at full locker depths. There
// is nothing locker-shaped to derive these from — the boxes we declare to the
// carrier are fixed per tier (see PARCEL_DECLARATION) and owe nothing to
// lockers. Weight is what actually binds on these carriers. Replaced when the
// Moov service brochures arrive (checklist item 9).
const CARRIER_USABLE_DEPTH_CM = { small: 8, medium: 19, large: 41 };

// Round to the centimetre-hundredth so derived depths are the exact figures
// quoted in the spec (19 * 0.85 is 16.150000000000002 in binary floating point)
// and comparisons don't turn on invisible dust.
const round2 = n => Math.round(n * 100) / 100;

/**
 * Usable depth of one container tier, in cm.
 *
 * Derived, never hardcoded: the margin is tuned against real data (§B10), so it
 * has to live in exactly one place.
 */
const usableDepthCm = (method, tier) =>
  isInPost(method)
    ? round2(LOCKER_DEPTH_CM[tier] * (1 - INPOST_MARGIN))
    : CARRIER_USABLE_DEPTH_CM[tier];

// Weight ceiling per tier, in grams — §A5.
//
// Every InPost compartment takes 15 kg, so weight never discriminates between
// InPost tiers; it is a hard cap that only bites at the very top of a large
// locker. On DPD/Yodel the bands are low and routinely decide the tier.
// VERIFY: checklist item 9 — the Moov brochures replace the carrier rows.
const INPOST_WEIGHT_LIMIT_G = 15000;

const WEIGHT_LIMIT_G = {
  [DPD_KEY]: { small: 2000, medium: 5000, large: 10000 },
  [YODEL_HOME_KEY]: { small: 1000, medium: 2000, large: 5000 },
  [YODEL_STORE_KEY]: { small: 1000, medium: 2000, large: 5000 },
  [INPOST_LOCKER_TO_HOME_KEY]: {
    small: INPOST_WEIGHT_LIMIT_G,
    medium: INPOST_WEIGHT_LIMIT_G,
    large: INPOST_WEIGHT_LIMIT_G,
  },
  [INPOST_LOCKER_TO_LOCKER_KEY]: {
    small: INPOST_WEIGHT_LIMIT_G,
    medium: INPOST_WEIGHT_LIMIT_G,
    large: INPOST_WEIGHT_LIMIT_G,
  },
};

// Dimensions and weight declared to Moov per tier — the per-tier MAXIMUMS, so
// the label carries some wiggle room. Only the tier lookup changes under this
// model; the values themselves are unchanged (§B5).
const PARCEL_DECLARATION = {
  [DPD_KEY]: {
    small: { width: 20, height: 10, length: 30, weight: 2 },
    medium: { width: 20, height: 10, length: 30, weight: 5 },
    large: { width: 20, height: 10, length: 30, weight: 10 },
  },
  [YODEL_HOME_KEY]: {
    small: { length: 50, width: 30, height: 20, weight: 1 },
    medium: { length: 50, width: 30, height: 20, weight: 2 },
    large: { length: 90, width: 40, height: 30, weight: 5 },
  },
};
PARCEL_DECLARATION[YODEL_STORE_KEY] = PARCEL_DECLARATION[YODEL_HOME_KEY];

// ---------------------------------------------------------------------------
// Depth of a parcel — §B2
// ---------------------------------------------------------------------------

/**
 * Depth in cm of a set of books packed flat in layers.
 *
 * Books that go one-per-layer (large sizes) each form their own layer. The rest
 * pair up two to a layer; sorting thickest-first and pairing neighbours
 * minimises the sum of per-layer maxima, and a leftover single is its own layer.
 *
 * Pairing across sizes is allowed — a small paperback beside a medium hardback —
 * and the layer takes the thicker of the two, which is the conservative reading.
 *
 * @param {Array<Object>} books - resolved books (see resolveBook)
 * @returns {number} depth in cm
 */
const parcelDepthCm = books => {
  const singles = books.filter(b => b.sideBySide === 1);
  const pairable = books.filter(b => b.sideBySide > 1).sort((a, b) => b.thicknessCm - a.thicknessCm);

  let depth = singles.reduce((sum, b) => sum + b.thicknessCm, 0);

  // Thickest-first, two at a time: the first of each pair is the thicker, so it
  // sets the layer.
  for (let i = 0; i < pairable.length; i += 2) {
    depth += pairable[i].thicknessCm;
  }

  return round2(depth);
};

const parcelWeightG = books => books.reduce((sum, b) => sum + b.weightG, 0);

/**
 * Cheap lower bound on the depth of a set of books, used to prune the solver's
 * search. Ignores that layers round up, so it never overestimates.
 */
const depthLowerBoundCm = books =>
  round2(books.reduce((sum, b) => sum + b.thicknessCm / b.sideBySide, 0));

// ---------------------------------------------------------------------------
// Tier selection
// ---------------------------------------------------------------------------

const tierIndex = tier => TIERS.indexOf(tier);

/** The dearer of two tiers. */
const maxTier = (a, b) => (tierIndex(a) >= tierIndex(b) ? a : b);

/**
 * Smallest tier whose weight limit covers this parcel, or null if it exceeds
 * every tier (the parcel must be split).
 */
const weightTierFor = (method, weightG) => {
  const limits = WEIGHT_LIMIT_G[method];
  return TIERS.find(tier => weightG <= limits[tier]) || null;
};

// ---------------------------------------------------------------------------
// The packing solver — §B3
// ---------------------------------------------------------------------------

// A basket needing more containers than it has books is impossible, so this
// bounds the search. Baskets are in the low tens.
const maxContainersFor = books => Math.max(1, books.length);

// Hard ceiling on basket size. The container enumeration is cubic in the book
// count, so this is a DoS guard, not a business rule: the pricing endpoints are
// reachable anonymously and a caller that claims a few hundred books can block
// the event loop for seconds or exhaust the heap. Real baskets are in the low
// tens (the spec's largest worked example is 28), and 60 books solves in ~20 ms.
// Raise it only alongside a cheaper solver.
const MAX_BOOKS_PER_BASKET = 60;

/**
 * Can these books go in one container of this tier?
 *
 * Both constraints apply: the layered depth must fit the tier's usable depth,
 * and the weight must be within what any tier of this method can carry (the
 * price may then lift to a dearer tier, but an over-limit parcel is simply
 * impossible).
 */
const fitsContainer = (method, tier, books) => {
  if (books.length === 0) {
    return true;
  }
  const withinDepth = parcelDepthCm(books) <= usableDepthCm(method, tier) + 1e-9;
  const withinWeight = weightTierFor(method, parcelWeightG(books)) !== null;
  return withinDepth && withinWeight;
};

/**
 * First-fit-decreasing: thickest book first, into the first container it fits.
 *
 * A heuristic, not a decision procedure — it can rarely miss a packing that
 * exists, and the solver then pays for the next combination up. That is
 * acceptable and deliberate; do not "fix" it into an exhaustive search.
 *
 * @returns {Array<Array<Object>>|null} books per container, or null if they
 *   don't all fit
 */
const assignToContainers = (method, tierCounts, books) => {
  const containers = [];
  TIERS.forEach(tier => {
    for (let i = 0; i < (tierCounts[tier] || 0); i += 1) {
      containers.push({ tier, books: [] });
    }
  });

  if (containers.length === 0) {
    return null;
  }

  // Largest first, so awkward books are placed while containers are empty.
  const ordered = books.slice().sort((a, b) => b.thicknessCm - a.thicknessCm);

  for (const book of ordered) {
    const target = containers.find(c => fitsContainer(method, c.tier, c.books.concat([book])));
    if (!target) {
      return null;
    }
    target.books.push(book);
  }

  // Combinations that leave a container empty are wasteful duplicates of a
  // cheaper combination, which the enumeration will also have produced.
  return containers.every(c => c.books.length > 0) ? containers.map(c => c.books) : null;
};

/**
 * Every combination of containers worth considering, cheapest-by-depth first.
 *
 * The depth-only price is a lower bound on the final price (the weight lift can
 * only raise it), so it is a sound ordering to prune against.
 */
const containerCombinations = (method, books) => {
  const rates = RATES[method];
  const limit = maxContainersFor(books);
  const lowerBound = depthLowerBoundCm(books);
  const combos = [];

  for (let large = 0; large <= limit; large += 1) {
    for (let medium = 0; medium + large <= limit; medium += 1) {
      for (let small = 0; small + medium + large <= limit; small += 1) {
        const count = small + medium + large;
        if (count === 0) {
          continue;
        }
        const capacity =
          small * usableDepthCm(method, 'small') +
          medium * usableDepthCm(method, 'medium') +
          large * usableDepthCm(method, 'large');
        if (capacity + 1e-9 < lowerBound) {
          continue;
        }
        combos.push({
          tierCounts: { small, medium, large },
          depthOnlyPrice: small * rates.small + medium * rates.medium + large * rates.large,
        });
      }
    }
  }

  return combos.sort((a, b) => a.depthOnlyPrice - b.depthOnlyPrice);
};

/**
 * The seller's floor: the biggest parcel size the seller picked for any book in
 * this parcel. The listing form asks sellers for a *parcel* size, and a seller
 * who says a book needs a large parcel gets at least a large parcel, whatever
 * the thickness table estimates. The depth model still decides how books
 * combine; it never prices a parcel below what its seller asked for.
 */
const sellerFloorTier = books => books.reduce((tier, book) => maxTier(tier, book.size), TIERS[0]);

/**
 * Price one assignment, applying the weight lift and the seller's floor per
 * parcel.
 *
 * A parcel's tier is the dearest of the container it was placed in, the tier
 * its weight demands (§A5) and the parcel size its seller picked. The container
 * keeps its own depth capacity; only the price moves.
 */
const priceAssignment = (method, containers, tierCounts) => {
  const rates = RATES[method];
  const tiers = [];
  TIERS.forEach(tier => {
    for (let i = 0; i < (tierCounts[tier] || 0); i += 1) {
      tiers.push(tier);
    }
  });

  const parcels = containers.map((booksInParcel, i) => {
    const weightG = parcelWeightG(booksInParcel);
    const tier = maxTier(
      maxTier(tiers[i], weightTierFor(method, weightG)),
      sellerFloorTier(booksInParcel)
    );
    return {
      tier,
      books: booksInParcel,
      depthCm: parcelDepthCm(booksInParcel),
      weightG,
      price: rates[tier],
    };
  });

  return {
    parcels,
    price: round2(parcels.reduce((sum, p) => sum + p.price, 0)),
  };
};

/**
 * Cheapest way to carry a basket with one delivery method.
 *
 * @param {string} method - a carrier delivery method key (not self-shipping)
 * @param {Array<Object>} rawBooks - one entry per physical book,
 *   `{ size, format, listingId? }`
 * @returns {{parcels: Array, price: number, alternatives: Array}}
 *   `price` is in major units. `alternatives` holds the next-cheapest distinct
 *   combinations for the "why this split" explainer (§A6), so the explanation
 *   stays consistent with what was charged.
 * @throws if the basket cannot be carried by this method at all
 */
const packBasket = (method, rawBooks, options) => {
  const { alternativeCount = 2 } = options || {};

  if (!RATES[method]) {
    throw new Error(`Unknown delivery method: ${method}`);
  }
  if (!rawBooks || rawBooks.length === 0) {
    throw new Error('Cannot pack an empty basket');
  }
  if (rawBooks.length > MAX_BOOKS_PER_BASKET) {
    throw new Error(`Cannot pack more than ${MAX_BOOKS_PER_BASKET} books in one order`);
  }
  const books = rawBooks.map(resolveBook);

  const combos = containerCombinations(method, books);
  const priced = [];
  let best = null;

  for (const combo of combos) {
    // Combinations arrive cheapest-first by depth-only price, which is a lower
    // bound on the final price. So once that bound reaches the best price we
    // have, nothing further can win — and everything beyond is strictly dearer,
    // i.e. exactly the runners-up the explainer wants. Keep going only until we
    // have enough of those.
    const canStillWin = !best || combo.depthOnlyPrice < best.price;
    const dearerFound = best ? priced.filter(c => c.price > best.price).length : 0;
    if (!canStillWin && dearerFound >= alternativeCount) {
      break;
    }

    const containers = assignToContainers(method, combo.tierCounts, books);
    if (!containers) {
      continue;
    }
    const candidate = priceAssignment(method, containers, combo.tierCounts);
    priced.push(candidate);
    if (!best || candidate.price < best.price) {
      best = candidate;
    }
  }

  if (!best) {
    throw new Error(`Basket cannot be shipped by ${method}: no container combination fits`);
  }

  // One entry per distinct shape — two assignments that buy the same containers
  // are the same answer to the buyer.
  const seenShapes = new Set();
  const alternatives = priced
    .filter(c => c.price > best.price)
    .sort((a, b) => a.price - b.price)
    .reduce((acc, c) => {
      const tiers = c.parcels.map(p => p.tier).sort();
      const shape = tiers.join('+');
      if (acc.length < alternativeCount && !seenShapes.has(shape)) {
        seenShapes.add(shape);
        acc.push({ price: c.price, tiers });
      }
      return acc;
    }, []);

  return { parcels: best.parcels, price: best.price, alternatives };
};

/**
 * Shipping price for a basket, or null when the method isn't purchasable.
 *
 * Self-shipping is priced by the seller, by book count, and never by depth
 * (§A3/§A8) — it is handled by the caller, not here.
 */
const basketShippingPrice = (method, rawBooks) => {
  if (method === SELF_SHIPPING_KEY || !RATES[method]) {
    return null;
  }
  return packBasket(method, rawBooks).price;
};

/**
 * The seller's own postage price for a listing, or null when they have not set
 * one.
 *
 * "Seller arranges postage" is only offered when the seller has named a price:
 * £0 is a real answer (a hand-over in person), but a blank or missing price is
 * not, and must not be advertised or charged as free. Shared by the listing
 * page, the cart and the server so all three agree.
 *
 * @param {number|string|null|undefined} value - listing publicData.selfShippingPrice
 * @returns {number|null} price in pounds
 */
const sellerPostagePrice = value => {
  if (value == null || (typeof value === 'string' && value.trim() === '')) {
    return null;
  }
  const price = Number(value);
  return Number.isFinite(price) && price >= 0 ? price : null;
};

/**
 * "Seller arranges postage" for an order: every book at its listing's postage
 * price, added up (ops, 2026-10-22). A seller's price is for posting one book,
 * so ten books are never sent for the price of one. Summed in pence so the
 * total has no float dust.
 *
 * @param {Array<{price: number|string|null|undefined, count: number}>} items -
 *   one entry per listing: its selfShippingPrice and how many copies
 * @returns {number|null} price in pounds, or null when any book has no price
 *   set (the method is then not on offer, never free)
 */
const sellerPostageTotal = items => {
  const prices = items.map(item => sellerPostagePrice(item.price));
  if (prices.length === 0 || prices.some(price => price == null)) {
    return null;
  }
  const pence = items.reduce((sum, item, i) => sum + Math.round(prices[i] * 100) * item.count, 0);
  return pence / 100;
};

// The tables above are deliberately NOT exported. They are inputs to the
// engine, not an API: anything that reads a thickness or a rate directly is
// re-implementing a decision this module already makes. The few named below
// are what callers and the tests actually need.
module.exports = {
  sellerPostagePrice,
  sellerPostageTotal,
  // Method keys
  DPD_KEY,
  YODEL_HOME_KEY,
  YODEL_STORE_KEY,
  INPOST_LOCKER_TO_HOME_KEY,
  INPOST_LOCKER_TO_LOCKER_KEY,
  SELF_SHIPPING_KEY,
  shippingOptionOrder,
  shipmentsForAdmin,
  isShippingOptionDisabled,

  // What we declare to the carrier for a given tier
  PARCEL_DECLARATION,

  // Engine
  MAX_BOOKS_PER_BASKET,
  resolveBook,
  parcelDepthCm,
  usableDepthCm,
  weightTierFor,
  packBasket,
  basketShippingPrice,
};
